import { Component, HostListener, ElementRef, EventEmitter, AfterViewInit, Output, Renderer2, ViewChild, OnDestroy } from '@angular/core';
import { Router, RouterLink, NavigationEnd, NavigationStart } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { AboutusComponent } from '../aboutus/aboutus.component';
import { ResourcesNavcardComponent } from '../resources-navcard/resources-navcard.component';
import { CareersNavcardComponent } from '../careers-navcard/careers-navcard.component';
import { SolutionNavcardComponent } from '../solution-navcard/solution-navcard.component';
import { GalleryNavcardComponent } from '../gallery-navcard/gallery-navcard.component';
import { ContactUsComponent } from '../contact-us/contact-us.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// Important: Import Bootstrap's Offcanvas
import { Offcanvas } from 'bootstrap';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgbDropdownModule,
    AboutusComponent,
    ResourcesNavcardComponent,
    CareersNavcardComponent,
    SolutionNavcardComponent,
    GalleryNavcardComponent,
    ContactUsComponent
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss'
})
export class NavbarComponent implements AfterViewInit, OnDestroy {

  @Output() contactClick = new EventEmitter<void>();

  @ViewChild('offcanvasElement') offcanvasRef!: ElementRef;

  isCollapsed: boolean = true;
  showAboutUs: boolean = false;
  showResources: boolean = false;
  showCareers: boolean = false;
  showSolutions: boolean = false;
  showGallery: boolean = false;

  isScrolled = false;

  private routerSubscription?: Subscription;

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    this.isScrolled = window.pageYOffset > 10;
  }

  constructor(
    public router: Router,
    private el: ElementRef,
    private renderer: Renderer2,
    private modalService: NgbModal
  ) { }

  ngAfterViewInit(): void {
    const nav = this.el.nativeElement.querySelector('.main-navbar');
    if (nav) {
      const h = nav.getBoundingClientRect().height;
      this.renderer.setStyle(document.documentElement, '--nav-height', `${Math.ceil(h)}px`);
    }

    const btn = this.el.nativeElement.querySelector('.contact-btn') as HTMLElement;
    if (btn) {
      btn.addEventListener('mousemove', (e: MouseEvent) => {
        const rect = btn.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100 + '%';
        const y = ((e.clientY - rect.top) / rect.height) * 100 + '%';

        btn.style.setProperty('--mx', x);
        btn.style.setProperty('--my', y);
      });
    }
  }

  openContactModal() {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      scrollable: true,
      windowClass: 'clean-modal-ui'
    });

    // Close all dropdowns
    this.closeAllDropdowns();
  }

  nav(route: string) {
    // Close mobile offcanvas first
    this.closeOffcanvas();

    // Close all mega-menu dropdowns
    this.closeAllDropdowns();

    if (route === '/') {
      if (this.router.url === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.router.navigate(['/']).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
    } else {
      this.router.navigate([`/${route}`]);
    }
  }

  private closeOffcanvas() {
    if (!this.offcanvasRef?.nativeElement) return;

    const offcanvasEl = this.offcanvasRef.nativeElement;
    const bsOffcanvas = Offcanvas.getInstance(offcanvasEl);

    if (bsOffcanvas) {
      bsOffcanvas.hide();

      // Use requestAnimationFrame to ensure the hide() has been processed
      requestAnimationFrame(() => {
        // Remove all backdrop elements immediately
        const backdrops = document.querySelectorAll('.offcanvas-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        // Restore body styles
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');

        // Dispose the offcanvas instance to prevent it from recreating backdrop
        bsOffcanvas.dispose();
      });
    }
  }

  private closeAllDropdowns() {
    this.showAboutUs = false;
    this.showResources = false;
    this.showCareers = false;
    this.showSolutions = false;
    this.showGallery = false;
  }

  toggleCareers() {
    this.showCareers = !this.showCareers;
    if (this.showCareers) {
      this.closeOtherDropdowns('careers');
    }
  }

  toggleResources() {
    this.showResources = !this.showResources;
    if (this.showResources) {
      this.closeOtherDropdowns('resources');
    }
  }

  toggleAboutUs() {
    this.showAboutUs = !this.showAboutUs;
    if (this.showAboutUs) {
      this.closeOtherDropdowns('aboutus');
    }
  }

  toggleSolutions() {
    this.showSolutions = !this.showSolutions;
    if (this.showSolutions) {
      this.closeOtherDropdowns('solutions');
    }
  }

  toggleGallery() {
    this.showGallery = !this.showGallery;
    if (this.showGallery) {
      this.closeOtherDropdowns('gallery');
    }
  }

  private closeOtherDropdowns(current: string) {
    if (current !== 'solutions') this.showSolutions = false;
    if (current !== 'resources') this.showResources = false;
    if (current !== 'careers') this.showCareers = false;
    if (current !== 'gallery') this.showGallery = false;
    if (current !== 'aboutus') this.showAboutUs = false;
  }

  ngOnInit() {
    window.addEventListener('resize', this.updateNavHeight.bind(this));

    // Subscribe to router events to close menu on navigation
    // Use NavigationStart to close dropdowns immediately (prevents them showing on new page)
    // Use NavigationEnd to clean up offcanvas backdrop after navigation completes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          // Close dropdowns immediately when navigation starts
          this.closeAllDropdowns();
        } else if (event instanceof NavigationEnd) {
          // Close offcanvas and clean up backdrop after navigation completes
          this.closeOffcanvas();
        }
      });
  }

  updateNavHeight() {
    const nav = this.el.nativeElement.querySelector('.main-navbar');
    if (nav) {
      const h = nav.getBoundingClientRect().height;
      this.renderer.setStyle(document.documentElement, '--nav-height', `${Math.ceil(h)}px`);
    }
  }

  ngOnDestroy() {
    // Clean up router subscription to prevent memory leaks
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}